import { ArrowLeft, ArrowRight, Sparkles, Loader2, AlertCircle, RefreshCw, Zap, MessageCircle } from 'lucide-react';
import { Card, CardHeader, CardBody, Badge } from '@neurograph/primitives';
import { useStudio } from '../providers/StudioContext';
import { StalenessIndicator } from '../components/StalenessIndicator';
import type { CreativeAngle } from '../types';

function AngleCardCompact({ angle, rank, onSelect }: { angle: CreativeAngle; rank: number; onSelect?: () => void }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3 w-full">
          <span className="text-sm font-bold text-primary-500">#{rank}</span>
          <h3 className="text-sm font-semibold text-neutral-900 flex-1">
            {angle.name || angle.angle_name || `Angle ${rank}`}
          </h3>
          {angle.archetype && (
            <Badge variant="info">{angle.archetype}</Badge>
          )}
          {angle.score !== undefined && (
            <span className="text-xs font-semibold text-primary-600">
              {angle.score}/100
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {/* Hook - Primary Focus */}
        {angle.hook && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle size={12} className="text-amber-600" />
              <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Hook</span>
            </div>
            <p className="text-sm font-medium text-neutral-800 leading-relaxed">
              &ldquo;{angle.hook}&rdquo;
            </p>
          </div>
        )}
        {angle.message && (
          <p className="text-sm text-neutral-500 mb-3 leading-relaxed">
            {angle.message}
          </p>
        )}
        <div className="space-y-1.5">
          {angle.emotional_lever && (
            <div className="flex gap-2 text-xs">
              <span className="font-medium text-neutral-400 min-w-[100px]">Emotional Lever</span>
              <span className="text-neutral-600">{angle.emotional_lever}</span>
            </div>
          )}
          {angle.strategic_rationale && (
            <div className="flex gap-2 text-xs">
              <span className="font-medium text-neutral-400 min-w-[100px]">Strategy</span>
              <span className="text-neutral-600">{angle.strategic_rationale}</span>
            </div>
          )}
        </div>
        {onSelect && (
          <button
            onClick={onSelect}
            className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <ArrowRight size={12} />
            Generate Script from this Hook
          </button>
        )}
      </CardBody>
    </Card>
  );
}

export function AnglesPanel() {
  const {
    angles,
    anglesGenerating,
    anglesStale,
    brief,
    generationError,
    setActivePanel,
    generateAngles,
    generateScript,
  } = useStudio();

  if (angles.length === 0 && !anglesGenerating) {
    return (
      <div className="text-center py-16 px-8">
        <Sparkles size={36} className="text-neutral-200 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-neutral-700 mb-2">No Hooks & Angles Generated</h3>
        <p className="text-neutral-500 mb-6 text-sm max-w-sm mx-auto">
          {brief
            ? 'Generate attention-grabbing hooks and creative angles grounded in your brief — each with a compelling hook, message, and strategic rationale.'
            : 'Create a brief first, then generate hooks & angles.'}
        </p>
        {generationError && (
          <div className="flex items-center gap-2 justify-center text-xs text-red-600 mb-4">
            <AlertCircle size={12} />
            <span>{generationError}</span>
          </div>
        )}
        <div className="flex items-center justify-center gap-3">
          {brief && (
            <>
              <button
                onClick={generateAngles}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors"
              >
                <Sparkles size={14} />
                Generate Hooks & Angles
              </button>
              <button
                onClick={() => setActivePanel('assets')}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-500 text-white rounded-lg text-sm font-semibold hover:bg-emerald-600 transition-colors"
                title="Skip to ad pack generation"
              >
                <Zap size={14} />
                Quick Ad Pack
              </button>
            </>
          )}
          <button
            onClick={() => setActivePanel('brief')}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-neutral-600 border border-neutral-200 rounded-lg hover:bg-neutral-50"
          >
            <ArrowLeft size={14} />
            Back to Brief
          </button>
        </div>
      </div>
    );
  }

  if (anglesGenerating) {
    return (
      <div className="text-center py-16 px-8">
        <Loader2 size={32} className="text-primary-500 mx-auto mb-4 animate-spin" />
        <h3 className="text-base font-semibold text-neutral-700 mb-1">Generating Angles</h3>
        <p className="text-sm text-neutral-400">Creating creative approaches from your brief…</p>
      </div>
    );
  }

  const handleGenerateScript = (angle: CreativeAngle) => {
    generateScript(angle);
    setActivePanel('script');
  };

  return (
    <div className="space-y-4">
      <StalenessIndicator isStale={anglesStale} />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-neutral-900">
            Hooks & Angles ({angles.length})
          </h2>
          <p className="text-xs text-neutral-500 mt-0.5">
            Select a hook to generate a script, or go directly to ad pack
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generateAngles}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-neutral-500 border border-neutral-200 rounded-lg hover:bg-neutral-50"
            title="Regenerate hooks & angles"
          >
            <RefreshCw size={12} />
            Regenerate
          </button>
          <button
            onClick={() => setActivePanel('script')}
            className="flex items-center gap-1.5 px-4 py-2 text-neutral-700 bg-white border border-neutral-200 rounded-lg text-sm font-medium hover:bg-neutral-50"
          >
            Script
            <ArrowRight size={14} />
          </button>
          <button
            onClick={() => setActivePanel('assets')}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-semibold hover:bg-emerald-600"
          >
            <Zap size={14} />
            Ad Pack
          </button>
        </div>
      </div>

      {generationError && (
        <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertCircle size={12} />
          <span>{generationError}</span>
        </div>
      )}

      {angles.map((angle, i) => (
        <AngleCardCompact
          key={angle.angle_id || i}
          angle={angle}
          rank={i + 1}
          onSelect={() => handleGenerateScript(angle)}
        />
      ))}
    </div>
  );
}
